import React, { useState, useEffect } from 'react';
import { Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Slide } from '@mui/material';

let notificationCallback = null;

export const showNotification = (message, type = 'info', duration = 3000) => {
  if (notificationCallback) {
    notificationCallback(message, type, duration);
  }
};

export const showConfirmation = (message, description = '') => {
  return new Promise((resolve) => {
    if (notificationCallback) {
      notificationCallback(message, 'confirm', 0, (confirmed) => {
        resolve(confirmed);
      }, description);
    } else {
      resolve(false);
    }
  });
};

const Notification = () => {
  const [open, setOpen] = useState(false);
  const [notification, setNotification] = useState({ message: '', type: 'info', duration: 3000 });
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    notificationCallback = (message, type, duration, callback, description) => {
      if (type === 'confirm') {
        setConfirmation({ message, description, callback });
      } else {
        setNotification({ message, type, duration: duration ?? 3000 });
        setOpen(true);
      }
    };
    return () => {
      notificationCallback = null;
    };
  }, []);

  const handleClose = () => {
    setOpen(false);
  };

  const handleConfirm = (confirmed) => {
    if (confirmation && confirmation.callback) {
      confirmation.callback(confirmed);
    }
    setConfirmation(null);
  };

  const alertRole = notification.type === 'error' ? 'alert' : 'status';
  const alertLive = notification.type === 'error' ? 'assertive' : 'polite';

  return (
    <>
      <Snackbar
        open={open}
        autoHideDuration={notification.duration || 3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={(props) => <Slide {...props} direction="left" />}
        sx={{ mt: { xs: 7, md: 2 }, mr: 1 }}
      >
        <Alert
          severity={notification.type}
          variant="filled"
          onClose={handleClose}
          sx={{
            width: '100%',
            borderRadius: 3,
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15)',
            fontWeight: 600,
            fontSize: '0.875rem',
            alignItems: 'center',
            '& .MuiAlert-icon': {
              fontSize: '1.25rem'
            }
          }}
          role={alertRole}
          aria-live={alertLive}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={Boolean(confirmation)}
        onClose={() => handleConfirm(false)}
        aria-describedby="global-confirmation-description"
        PaperProps={{
          sx: {
            borderRadius: 4,
            maxWidth: 420,
            p: 1
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Confirm Action</DialogTitle>
        <DialogContent>
          <Typography id="global-confirmation-description" sx={{ fontSize: '0.9375rem', color: '#1e293b' }}>
            {confirmation?.message}
          </Typography>
          {confirmation?.description && (
            <Typography sx={{ mt: 1.5, fontSize: '0.8125rem', color: '#64748b' }}>
              {confirmation.description}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ pt: 1, px: 2, pb: 2, gap: 1 }}>
          <Button 
            onClick={() => handleConfirm(false)} 
            sx={{ color: '#64748b', fontWeight: 600, px: 2 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => handleConfirm(true)} 
            variant="contained" 
            sx={{
              bgcolor: '#059669',
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              '&:hover': { bgcolor: '#047857' }
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Notification;
